import { 
  NormalizedAction, 
  NormalizedKeyword
} from '@/types/action-types';

/**
 * Normalize any action response into a consistent structure
 * This is the single point of transformation for all action data
 */
export function normalizeAction(action: any, adActions?: any[]): NormalizedAction | null {
  if (!action) return null;
  
  // Basic action properties
  const normalized: NormalizedAction = {
    id: action.id,
    actionType: normalizeActionType(action.action_type),
    campaignId: action.campaign_id,
    campaignName: action.campaign_name || `Campaign #${action.campaign_id}`,
    createdAt: action.created_at,
    isUndone: !!action.is_undone,
    reason: action.reason || extractReasonFromDetails(action),
    original: action
  };
  
  // Handle action-specific data
  const actionType = String(action.action_type || '').toLowerCase();
  
  // Extract keywords for keyword-related actions
  if (actionType.includes('keyword')) {
    normalized.keywords = extractKeywords(action);
  }
  
  // Extract budget info for budget actions
  if (actionType.includes('budget')) {
    normalized.budget = extractBudgetInfo(action);
  }
  
  // Include ad-grouped actions if provided
  if (adActions && adActions.length > 0) {
    normalized.adGroupedActions = adActions;
  }
  
  return normalized;
}

/**
 * Standardize action type names
 */
function normalizeActionType(actionType: string): string {
  if (!actionType) return '';
  
  // Convert to uppercase for consistent comparison since backend sends uppercase
  const type = actionType.toUpperCase();
  
  // Handle specific action types that come from backend
  switch (type) {
    case 'ADD_EXACT_MATCH_KEYWORD':
      return 'ADD_EXACT_MATCH_KEYWORD';
    case 'ADD_NEGATIVE_KEYWORD':
      return 'ADD_NEGATIVE_KEYWORD';
    case 'ADD_KEYWORD':
      return 'ADD_KEYWORDS'; // Convert singular to plural for consistency
    case 'PAUSE_KEYWORD':
      return 'PAUSE_KEYWORDS'; // Convert singular to plural for consistency  
    case 'DELETE_KEYWORD':
      return 'DELETE_KEYWORDS'; // Convert singular to plural for consistency
    case 'SET_BUDGET':
      return 'SET_BUDGET';
    case 'SET_TCPA':
      return 'SET_TCPA';
    default: {
      // Fallback to checking substrings for compatibility
      const lowerType = actionType.toLowerCase();
      
      // Handle negative keyword actions (more specific first)
      if (lowerType.includes('add_negative_keyword')) {
        if (lowerType.includes('for_ad')) return 'ADD_NEGATIVE_KEYWORD_FOR_AD';
        if (lowerType.includes('for_campaign')) return 'ADD_NEGATIVE_KEYWORD_FOR_CAMPAIGN';
        return 'ADD_NEGATIVE_KEYWORD';
      }
      
      // Handle regular keyword actions  
      if (lowerType.includes('add_exact_match_keyword')) return 'ADD_EXACT_MATCH_KEYWORD';
      if (lowerType.includes('add_keyword')) return 'ADD_KEYWORDS';
      if (lowerType.includes('pause_keyword')) return 'PAUSE_KEYWORDS';
      if (lowerType.includes('delete_keyword')) return 'DELETE_KEYWORDS';
      if (lowerType.includes('set_budget')) return 'SET_BUDGET';
      
      // Default to uppercase version of original
      return actionType.toUpperCase();
    }
  }
}

/**
 * Extract reason from various places in the action object
 */
function extractReasonFromDetails(action: any): string | undefined {
  if (action.reason) return action.reason;
  if (action.details?.reason) return action.details.reason;
  if (action.details?.recommendation_reason) return action.details.recommendation_reason;
  
  // Try to extract from log
  if (action.log) {
    const reasonMatch = action.log.match(/because\s+(.+?)(?:\.|\))/i);
    if (reasonMatch && reasonMatch[1]) {
      return reasonMatch[1].trim();
    }
  }
  
  // Default reasons based on action type
  const actionType = String(action.action_type || '').toLowerCase();
  if (actionType.includes('add_exact_match_keyword')) return 'To improve targeting precision';
  if (actionType.includes('add_keyword')) return 'To improve campaign performance';
  if (actionType.includes('pause_keyword')) return 'Due to poor performance';
  if (actionType.includes('delete_keyword')) return 'Zero impressions';
  
  return undefined;
}

/**
 * Extract all keywords from an action object, regardless of action type
 * This handles the various places keywords might be stored
 */
function extractKeywords(action: any): NormalizedKeyword[] {
  const actionType = String(action.action_type || '').toLowerCase();
  const keywords: NormalizedKeyword[] = [];
  
  // ADD_KEYWORDS, ADD_EXACT_MATCH_KEYWORD, or ADD_NEGATIVE_KEYWORD: Check after_state and details
  if (actionType.includes('add_keyword') || actionType.includes('add_exact_match_keyword') || actionType.includes('add_negative_keyword')) {
    const status = actionType.includes('negative') ? 'NEGATIVE' : 'ACTIVE';
    
    // From after_state.added_keywords (primary source)
    if (action.after_state?.added_keywords && Array.isArray(action.after_state.added_keywords)) {
      for (const kw of action.after_state.added_keywords) {
        if (!kw) continue;
        keywords.push(normalizeKeyword(kw, status));
      }
    }
    
    // From after_state.added_negative_keywords (for negative keyword actions)
    if (actionType.includes('negative') && action.after_state?.added_negative_keywords && Array.isArray(action.after_state.added_negative_keywords)) {
      for (const kw of action.after_state.added_negative_keywords) {
        if (!kw) continue;
        // Convert the negative keyword format to our standard format
        const convertedKw = {
          text: kw.keyword || kw.text,
          match_type: kw.match_type,
          ad_id: kw.googleads_ad_group_id || kw.ad_id,
          ad_name: kw.ad_name
        };
        keywords.push(normalizeKeyword(convertedKw, 'NEGATIVE'));
      }
    }
    
    // From details.keywords (fallback)
    if (action.details?.keywords && Array.isArray(action.details.keywords)) {
      for (const kw of action.details.keywords) {
        if (!kw) continue;
        keywords.push(normalizeKeyword(kw, status));
      }
    }
    
    // From details.negative_keywords (for negative keyword actions)
    if (actionType.includes('negative') && action.details?.negative_keywords && Array.isArray(action.details.negative_keywords)) {
      for (const kw of action.details.negative_keywords) {
        if (!kw) continue;
        keywords.push(normalizeKeyword(kw, 'NEGATIVE'));
      }
    }
  }
  
  // PAUSE_KEYWORDS or DELETE_KEYWORDS: Check before_state and details
  if (actionType.includes('pause_keyword') || actionType.includes('delete_keyword')) {
    // From single keyword in before_state
    if (action.before_state?.keyword_text || action.before_state?.text) {
      keywords.push(normalizeKeyword({
        id: action.before_state.keyword_id || action.before_state.id || 0,
        text: action.before_state.keyword_text || action.before_state.text,
        match_type: action.before_state.match_type,
        ad_id: action.before_state.ad_id,
        ad_name: action.before_state.ad_name,
        ad_url: action.before_state.ad_url,
        performance_metrics: action.before_state.performance_metrics
      }, actionType.includes('pause_keyword') ? 'PAUSED' : 'REMOVED'));
    }
    
    // From before_state.keywords array
    if (action.before_state?.keywords && Array.isArray(action.before_state.keywords)) {
      for (const kw of action.before_state.keywords) {
        if (!kw) continue;
        keywords.push(normalizeKeyword(kw, 
          actionType.includes('pause_keyword') ? 'PAUSED' : 'REMOVED'));
      }
    }
    
    // From details.deleted_keywords or details.paused_keywords
    const keywordsArray = actionType.includes('delete_keyword') 
      ? action.details?.deleted_keywords 
      : action.details?.paused_keywords;
      
    if (keywordsArray && Array.isArray(keywordsArray)) {
      for (const kw of keywordsArray) {
        if (!kw) continue;
        keywords.push(normalizeKeyword(kw, 
          actionType.includes('pause_keyword') ? 'PAUSED' : 'REMOVED'));
      }
    }
  }
  
  // Fallback: Extract from top-level keywords array (provided by backend grouped API)
  if (keywords.length === 0 && action.keywords && Array.isArray(action.keywords)) {
    const status = actionType.includes('negative') ? 'NEGATIVE' : 
                   actionType.includes('pause') ? 'PAUSED' : 'ACTIVE';
    
    for (const keywordText of action.keywords) {
      if (typeof keywordText === 'string') {
        keywords.push(normalizeKeyword({
          text: keywordText,
          match_type: 'EXACT', // Default for negative keywords from backend
        }, status));
      }
    }
  }
  
  // Deduplicate keywords by text
  return deduplicateKeywords(keywords);
}

/**
 * Normalize a keyword from any source into a consistent structure
 */
function normalizeKeyword(keyword: any, status: string = 'ACTIVE'): NormalizedKeyword {
  if (typeof keyword === 'string') {
    // Handle string keywords
    return {
      id: Math.floor(Math.random() * 1000000), // Generate a temporary ID for selection
      text: keyword,
      matchType: 'BROAD',
      status,
      labels: status === 'ACTIVE' ? ['New Keyword'] : 
              status === 'PAUSED' ? ['Paused', 'Low Performance'] : 
              ['Zero Impressions']
    };
  }
  
  // Handle object keywords
  return {
    id: keyword.id || keyword.keyword_id || Math.floor(Math.random() * 1000000),
    text: keyword.text || keyword.keyword_text || 'Unknown Keyword',
    matchType: keyword.match_type || 'BROAD',
    adId: keyword.ad_id,
    adName: keyword.ad_name || (keyword.ad_id ? `Ad #${keyword.ad_id}` : undefined),
    adUrl: keyword.ad_url,
    status,
    metrics: keyword.performance_metrics ? {
      impressions: keyword.performance_metrics.impressions || 0,
      clicks: keyword.performance_metrics.clicks || 0,
      cost: keyword.performance_metrics.cost || 0,
      ctr: keyword.performance_metrics.ctr || 0,
      conversions: keyword.performance_metrics.conversions || 0
    } : undefined,
    labels: keyword.labels || (
      status === 'ACTIVE' ? ['New Keyword'] : 
      status === 'PAUSED' ? ['Paused', 'Low Performance'] : 
      ['Zero Impressions']
    ),
    reason: keyword.reason
  };
}

/**
 * Deduplicate keywords by ID first, falling back to text - matches the approach in task-inbox-grouping.ts
 */
function deduplicateKeywords(keywords: NormalizedKeyword[]): NormalizedKeyword[] {
  const uniqueKeywords = new Map<string, NormalizedKeyword>();
  
  for (const keyword of keywords) {
    // Use ID as primary deduplication key, fall back to text
    const keywordKey = keyword.id ? `id-${keyword.id}` : `text-${keyword.text}`;
    
    if (!uniqueKeywords.has(keywordKey)) {
      uniqueKeywords.set(keywordKey, keyword);
    }
  }
  
  return Array.from(uniqueKeywords.values());
}

/**
 * Group keywords by ad ID for display
 */
export function groupKeywordsByAd(keywords: NormalizedKeyword[]): Record<string, {
  adId: string | number;
  adName: string;
  adUrl?: string;
  adSlug?: string;
  internalUrl?: string;
  keywords: NormalizedKeyword[];
}> {
  const keywordsByAd: Record<string, {
    adId: string | number;
    adName: string;
    adUrl?: string;
    adSlug?: string;
    internalUrl?: string;
    keywords: NormalizedKeyword[];
  }> = {};
  
  for (const keyword of keywords) {
    const adId = keyword.adId?.toString() || 'default';
    
    if (!keywordsByAd[adId]) {
      keywordsByAd[adId] = {
        adId,
        adName: keyword.adName || `Ad #${adId}`,
        adUrl: keyword.adUrl,
        adSlug: keyword.adSlug,
        internalUrl: keyword.internalUrl,
        keywords: []
      };
    }
    
    keywordsByAd[adId].keywords.push(keyword);
  }
  
  // If we have no ad groups, create a default group
  if (Object.keys(keywordsByAd).length === 0 && keywords.length > 0) {
    keywordsByAd['default'] = {
      adId: 'default',
      adName: 'All Ads',
      keywords: [...keywords]
    };
  }
  
  return keywordsByAd;
}

/**
 * Extract budget information from action
 */
function extractBudgetInfo(action: any): { before: number; after: number; currency: string; } | undefined {
  if (!action) return undefined;
  
  const beforeBudget = 
    action.before_state?.budget || 
    action.details?.before_budget || 
    action.budget_before;
    
  const afterBudget = 
    action.after_state?.budget || 
    action.details?.after_budget || 
    action.budget_after;
  
  if (beforeBudget === undefined && afterBudget === undefined) {
    return undefined;
  }
  
  return {
    before: beforeBudget || 0,
    after: afterBudget || 0,
    currency: action.currency || '$'
  };
}